import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Plus, X, Save, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function NotificationManager() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        id: '',
        service_id: '',
        template_id: 'default', // Legacy compatible
        public_key: 'default', // Legacy compatible
        to_emails: [] as string[],
        cc_emails: [] as string[],
        is_active: true
    });
    const [newToEmail, setNewToEmail] = useState("");
    const [newCcEmail, setNewCcEmail] = useState("");
    const { toast } = useToast();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const { data, error } = await supabase
                .from('notification_settings' as any)
                .select('*')
                .limit(1)
                .maybeSingle();

            if (error) throw error;

            if (data) {
                const d = data as any;
                setSettings({
                    id: d.id,
                    service_id: d.service_id || '',
                    template_id: d.template_id || 'default',
                    public_key: d.public_key || 'default',
                    to_emails: d.to_emails || [],
                    cc_emails: d.cc_emails || [],
                    is_active: d.is_active
                });
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddEmail = (type: 'to' | 'cc') => {
        const email = type === 'to' ? newToEmail : newCcEmail;
        if (!email || !email.includes('@')) {
            toast({ title: "Invalid email", variant: "destructive" });
            return;
        }

        if (type === 'to') {
            if (settings.to_emails.includes(email)) return;
            setSettings(prev => ({ ...prev, to_emails: [...prev.to_emails, email] }));
            setNewToEmail("");
        } else {
            if (settings.cc_emails.includes(email)) return;
            setSettings(prev => ({ ...prev, cc_emails: [...prev.cc_emails, email] }));
            setNewCcEmail("");
        }
    };

    const handleRemoveEmail = (type: 'to' | 'cc', email: string) => {
        if (type === 'to') {
            setSettings(prev => ({ ...prev, to_emails: prev.to_emails.filter(e => e !== email) }));
        } else {
            setSettings(prev => ({ ...prev, cc_emails: prev.cc_emails.filter(e => e !== email) }));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                service_id: settings.service_id,
                template_id: settings.template_id,
                public_key: settings.public_key,
                to_emails: settings.to_emails,
                cc_emails: settings.cc_emails,
                is_active: settings.is_active
            };

            if (settings.id) {
                const { error } = await supabase
                    .from('notification_settings' as any)
                    .update(payload)
                    .eq('id', settings.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('notification_settings' as any)
                    .insert([payload]);
                if (error) throw error;
            }

            toast({ title: "Settings saved successfully!" });
            fetchSettings();
        } catch (error: any) {
            console.error('Error saving settings:', error);
            toast({
                title: "Error saving settings",
                description: error.message,
                variant: "destructive"
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading settings...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Mail className="h-6 w-6" />
                        Notification Settings (Gmail)
                    </h2>
                    <p className="text-muted-foreground">Configure free email notifications using your Gmail account.</p>
                </div>
                <Button onClick={handleSave} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recipients */}
                <Card className="order-2 md:order-1">
                    <CardHeader>
                        <CardTitle>Recipients</CardTitle>
                        <CardDescription>Who receives the emails?</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-3">
                            <Label>To Recipients (Admin)</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={newToEmail}
                                    onChange={(e) => setNewToEmail(e.target.value)}
                                    placeholder="admin@vistaphotographya.com"
                                />
                                <Button size="icon" variant="outline" onClick={() => handleAddEmail('to')}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.to_emails.map((email) => (
                                    <Badge key={email} variant="secondary" className="flex items-center gap-1 pl-2 pr-1 py-1">
                                        {email}
                                        <button
                                            onClick={() => handleRemoveEmail('to', email)}
                                            className="ml-1 hover:bg-destructive/10 rounded-full p-0.5"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <Label>CC Recipients</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={newCcEmail}
                                    onChange={(e) => setNewCcEmail(e.target.value)}
                                    placeholder="assistant@gmail.com"
                                />
                                <Button size="icon" variant="outline" onClick={() => handleAddEmail('cc')}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.cc_emails.map((email) => (
                                    <Badge key={email} variant="outline" className="flex items-center gap-1 pl-2 pr-1 py-1">
                                        {email}
                                        <button
                                            onClick={() => handleRemoveEmail('cc', email)}
                                            className="ml-1 hover:bg-destructive/10 rounded-full p-0.5"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 pt-4 border-t">
                            <Switch
                                id="active-mode"
                                checked={settings.is_active}
                                onCheckedChange={(checked) => setSettings(s => ({ ...s, is_active: checked }))}
                            />
                            <Label htmlFor="active-mode">Enable Email Notifications</Label>
                        </div>
                    </CardContent>
                </Card>

                {/* Configuration */}
                <Card className="order-1 md:order-2">
                    <CardHeader>
                        <CardTitle>Google Script Configuration</CardTitle>
                        <CardDescription>
                            Connect your Gmail account for free sending.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Google Script Web App URL</Label>
                            <Input
                                value={settings.service_id}
                                onChange={(e) => setSettings(s => ({ ...s, service_id: e.target.value }))}
                                placeholder="https://script.google.com/macros/s/..."
                            />
                            <p className="text-xs text-muted-foreground">
                                Paste the URL deployed from your Google Script below.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Setup Instructions */}
            <Card className="bg-blue-50 border-blue-100">
                <CardContent className="pt-6 flex gap-4">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div className="space-y-4 w-full">
                        <div className="space-y-2">
                            <h4 className="font-medium text-blue-900">Setup Instructions (1 Minute)</h4>
                            <ol className="list-decimal list-inside text-sm text-blue-800 space-y-2">
                                <li>Go to <a href="https://script.google.com" target="_blank" className="underline font-bold">script.google.com</a> and click "New Project".</li>
                                <li>Paste the code below into the editor (replace everything):
                                    <pre className="mt-2 mb-2 bg-gray-100 p-3 text-xs rounded border overflow-x-auto select-all cursor-pointer font-mono text-black" onClick={(e) => {
                                        navigator.clipboard.writeText(e.currentTarget.innerText);
                                        toast({ title: "Code Copied!" });
                                    }}>
                                        {`function doPost(e) {
  try {
    // Robust parsing for text/plain or json
    var data = JSON.parse(e.postData.contents);
    var to = data.to_email;
    var cc = data.cc_email || '';
    var subject = data.subject || 'New Notification';
    var htmlBody = data.message_html;
    
    if(!to) return ContentService.createTextOutput("No Recipient");

    GmailApp.sendEmail(to, subject, '', {
      htmlBody: htmlBody,
      cc: cc
    });
    
    return ContentService.createTextOutput("Sent Successfully");
  } catch(err) {
    return ContentService.createTextOutput("Error: " + err.toString());
  }
}`}
                                    </pre>
                                </li>
                                <li>Click <strong>Deploy</strong> &rarr; <strong>New Deployment</strong>.</li>
                                <li>Select type: <strong>Web App</strong>.</li>
                                <li>Configuration: Use <strong>"Me"</strong> for execute, and <strong>"Anyone"</strong> (Who has access).</li>
                                <li>Click Deploy, copy the <strong>Web App URL</strong>, and paste it above.</li>
                            </ol>

                            <div className="mt-4 pt-4 border-t border-blue-200">
                                <p className="text-xs text-blue-800 font-semibold mb-1">First Time Database Setup:</p>
                                <p className="text-xs text-blue-700 mb-1">Run this SQL if you see "Table not found" errors.</p>
                                <pre className="mt-1 bg-gray-50 border-blue-200 border p-2 text-xs rounded overflow-x-auto select-all cursor-pointer text-gray-500" onClick={(e) => {
                                    navigator.clipboard.writeText(e.currentTarget.innerText);
                                    toast({ title: "SQL Copied!" });
                                }}>
                                    {`drop table if exists notification_settings;
create table notification_settings (
  id uuid primary key default uuid_generate_v4(),
  service_id text,
  template_id text,
  public_key text,
  to_emails text[],
  cc_emails text[],
  is_active boolean default true
);
alter table notification_settings enable row level security;
create policy "Allow admins" on notification_settings for all to authenticated using (true) with check (true);
-- CRITICAL: This policy enables Booking/Review forms (Anonymous users) to read the script URL.
create policy "Public Access" on notification_settings for select to anon using (true);`}
                                </pre>
                            </div>
                        </div>

                        {/* Test Button */}
                        <div className="pt-4 border-t border-blue-200">
                            <Button
                                type="button"
                                variant="default"
                                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                                onClick={async () => {
                                    if (!settings.service_id || !settings.to_emails.length) {
                                        toast({ title: "Configure URL and Recipient first.", variant: "destructive" });
                                        return;
                                    }

                                    toast({ title: "Sending test...", description: "Check your inbox in a moment." });

                                    try {
                                        // Manual test fetch
                                        await fetch(settings.service_id, {
                                            method: 'POST',
                                            mode: 'no-cors',
                                            headers: { 'Content-Type': 'text/plain' },
                                            body: JSON.stringify({
                                                subject: "Test Notification",
                                                // If the script just blindly emails what it gets,
                                                // and my `sendNotification` wraps it...
                                                // The RAW test bypasses the wrapper.
                                                // So the RAW test will look UGLY (Plain HTML).
                                                // That is fine. It proves connection.
                                                title: "System Operational",
                                                message_html: "<h3>Test Successful</h3><p>This is a raw connection test. Real emails will have the premium template.</p>",
                                                to_email: settings.to_emails.join(','),
                                                cc_email: settings.cc_emails.join(',')
                                            })
                                        });
                                        toast({ title: "Sent!", description: "Request dispatched to Google Script." });
                                    } catch (e: any) {
                                        console.error(e);
                                        toast({ title: "Error", description: e.message, variant: "destructive" });
                                    }
                                }}
                            >
                                Send Test Email
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
